import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Mail, Lock, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatApiErrorDetail, homeForRole } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-cyan-700 text-white flex items-center justify-center mx-auto mb-4"><KeyRound className="h-7 w-7" /></div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-8">{children}</div>
        <p className="text-center text-xs text-slate-400 mt-6"><Link to="/schulungen/login" className="inline-flex items-center gap-1 hover:text-cyan-700"><ArrowLeft className="h-3 w-3" /> Zurück zum Login</Link></p>
      </div>
    </div>
  );
}

export function PasswortVergessen() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { document.title = "Passwort vergessen | Schulungsportal"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/auth/reset-anfordern`, { email }); } catch { /* generic */ }
    setDone(true); setLoading(false);
  };

  return (
    <Shell title="Passwort vergessen" subtitle="Schulungsportal – Firmen & Teilnehmende" testId="passwort-vergessen-page">
      {done ? (
        <div className="text-center" data-testid="reset-anfrage-done">
          <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-3" />
          <p className="font-semibold text-slate-800">Anfrage übermittelt</p>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">Aus Sicherheitsgründen versenden wir keine E-Mails. <strong>Teilnehmende</strong> erhalten den Link zum Zurücksetzen von ihrem Arbeitgeber (Firmenportal), <strong>Firmen</strong> von der Kursleitung. Bitte melden Sie sich dort kurz – die Anfrage ist bereits sichtbar.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <p className="text-sm text-slate-600 leading-relaxed">Geben Sie Ihre E-Mail-Adresse ein. Ihr Arbeitgeber (bei Teilnehmenden) bzw. die Kursleitung (bei Firmen) sieht die Anfrage und sendet Ihnen einen Link zum Zurücksetzen.</p>
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail</Label>
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-10 rounded-xl" data-testid="reset-email" /></div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 font-semibold" data-testid="reset-anfordern-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset anfordern"}</Button>
        </form>
      )}
    </Shell>
  );
}

export function PasswortReset() {
  const { token } = useParams();
  const { setSession, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Neues Passwort | Schulungsportal";
    axios.get(`${API}/auth/reset/${token}`).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
    if (pw !== pw2) return setError("Die Passwörter stimmen nicht überein.");
    setLoading(true);
    try {
      logout();
      const { data } = await axios.post(`${API}/auth/reset/${token}`, { passwort: pw }, { withCredentials: true });
      setSession(data);
      navigate(homeForRole(data.role), { replace: true });
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail, "Zurücksetzen fehlgeschlagen")); }
    setLoading(false);
  };

  return (
    <Shell title="Neues Passwort festlegen" subtitle="Schulungsportal" testId="passwort-reset-page">
      {invalid || (info && !info.gueltig) ? (
        <div className="text-center" data-testid="reset-invalid">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-slate-800">{info?.used ? "Dieser Link wurde bereits verwendet" : "Dieser Link ist ungültig oder abgelaufen"}</p>
          <p className="text-sm text-slate-500 mt-2">Bitte fordern Sie einen neuen Link an.</p>
          <Link to="/schulungen/passwort-vergessen" className="inline-block mt-4 text-cyan-700 font-semibold hover:underline">Neuen Link anfordern</Link>
        </div>
      ) : !info ? <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div> : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-4 text-sm text-slate-700">Hallo <strong>{info.name}</strong>, legen Sie ein neues Passwort für <strong>{info.email}</strong> fest.</div>
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-xl border border-red-200" data-testid="reset-error">{error}</div>}
          <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Neues Passwort (min. 8 Zeichen)</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 pl-10 rounded-xl" data-testid="reset-pw" /></div></div>
          <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Passwort wiederholen</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-12 pl-10 rounded-xl" data-testid="reset-pw2" /></div></div>
          <Button type="submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 font-semibold" data-testid="reset-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Passwort speichern & anmelden"}</Button>
        </form>
      )}
    </Shell>
  );
}
